import { c as _c } from "react/compiler-runtime";
import React, { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import ButtonComponent from '../button/button/button.component';
import { ButtonType } from '../button/types/ButtonType';
import { FilterElement } from '@spa/lib/tracking/interactiveTrackingEventSchema';
import ImageComponent from '@spa/components/image-component/image.component';
import { LocaleContext } from '@spa/lib/react/context/LocaleContext';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { SelectedFacet } from '../facets/interfaces';
import formatPriceWithCurrency from '../../lib/formatPriceWithCurrency';
import { formatSliderValue } from '../facets/filterActionTracking';
import selectCurrencyIso from '../../lib/store/settings/selectors/selectCurrencyIso';
import styles from './selected-facets.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
import updateSelectedFacetsQuery from './updateSelectedFacetsQuery';
import useFilterActionPageType from '../facets/useFilterActionPageType';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';
import { useSelector } from 'react-redux';
export interface Props {
  readonly selectedFacets: ReadonlyArray<SelectedFacet>;
}
function SelectedFacetsComponent(t0) {
  const $ = _c(32);
  const {
    selectedFacets
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useRuntimeSearchParams();
  const [selectedState, setSelectedState] = useState(null);
  const locale = useContext(LocaleContext);
  const currencyIso = useSelector(selectCurrencyIso);
  const {
    formatMessage
  } = useNamespacedIntl("selected-facets");
  const {
    formatMessage: facetFormatMessage
  } = useNamespacedIntl("facet");
  const pageType = useFilterActionPageType();
  const userSelectedFacets = selectedState !== null && selectedState.basedOn === selectedFacets ? selectedState.facets : selectedFacets;
  let t1;
  if ($[0] !== facetFormatMessage || $[1] !== pageType) {
    t1 = (selectedFacet, value) => {
      let filterElement = "checkbox";
      let filterValue = value;
      if (selectedFacet.type === "range") {
        filterElement = "slider";
        filterValue = formatSliderValue(selectedFacet.from, selectedFacet.to);
      } else {
        if (selectedFacet.type === "boolean") {
          filterElement = "toggle";
          filterValue = false;
        }
      }
      tracker.filterAction({
        filter_category: facetFormatMessage(`facetNames.${selectedFacet.code}`),
        filter_element: filterElement,
        filter_interaction: "remove",
        filter_value: filterValue,
        page_type: pageType
      });
    };
    $[0] = facetFormatMessage;
    $[1] = pageType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const trackRemovedFacet = t1;
  let t2;
  if ($[3] !== pathname || $[4] !== router || $[5] !== searchParams || $[6] !== selectedFacets || $[7] !== trackRemovedFacet || $[8] !== userSelectedFacets) {
    t2 = () => {
      const newSelectedFacets = [];
      userSelectedFacets.forEach(selectedFacet_0 => {
        if (selectedFacet_0.type === "range") {
          trackRemovedFacet(selectedFacet_0, "");
        } else {
          selectedFacet_0.values.forEach(t3 => {
            const {
              label
            } = t3;
            return trackRemovedFacet(selectedFacet_0, label);
          });
        }
      });
      setSelectedState({
        facets: newSelectedFacets,
        basedOn: selectedFacets
      });
      updateSelectedFacetsQuery(newSelectedFacets, router, pathname, searchParams);
    };
    $[3] = pathname;
    $[4] = router;
    $[5] = searchParams;
    $[6] = selectedFacets;
    $[7] = trackRemovedFacet;
    $[8] = userSelectedFacets;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const removeAllFilters = t2;
  let t3;
  if ($[10] !== pathname || $[11] !== router || $[12] !== searchParams || $[13] !== selectedFacets || $[14] !== trackRemovedFacet || $[15] !== userSelectedFacets) {
    t3 = (facetToRemove, selectedFacetCode, label_0) => {
      const newSelectedFacets_0 = userSelectedFacets.reduce((acc, facet) => {
        if (facet.code === facetToRemove.code) {
          if (facet.type !== "range") {
            const filteredValues = facet.values.filter(value_0 => value_0.code !== selectedFacetCode);
            if (filteredValues.length > 0) {
              acc.push({
                ...facet,
                values: filteredValues
              });
            }
          }
        } else {
          acc.push(facet);
        }
        return acc;
      }, []);
      trackRemovedFacet(facetToRemove, label_0);
      setSelectedState({
        facets: newSelectedFacets_0,
        basedOn: selectedFacets
      });
      updateSelectedFacetsQuery(newSelectedFacets_0, router, pathname, searchParams);
    };
    $[10] = pathname;
    $[11] = router;
    $[12] = searchParams;
    $[13] = selectedFacets;
    $[14] = trackRemovedFacet;
    $[15] = userSelectedFacets;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const removeSelectedFacet = t3;
  let t4;
  if ($[17] !== currencyIso || $[18] !== locale) {
    t4 = price => formatPriceWithCurrency(price, currencyIso, locale, true);
    $[17] = currencyIso;
    $[18] = locale;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const formatPrice = t4;
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ImageComponent src="/assets/icons/close-icon-white.svg" alt="close-icon" width={17} height={16} />;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const crossIcon = t5;
  let t6;
  if ($[21] !== facetFormatMessage || $[22] !== removeSelectedFacet) {
    t6 = (selectedFacet_1, code, label_1) => <button className={styles["selected-facets__selected-facet"]} key={code} onClick={() => removeSelectedFacet(selectedFacet_1, code, label_1)}>{selectedFacet_1.type === "boolean" ? facetFormatMessage(`facetNames.${selectedFacet_1.code}`) : label_1}{crossIcon}</button>;
    $[21] = facetFormatMessage;
    $[22] = removeSelectedFacet;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const selectedFacetButton = t6;
  let t7;
  if ($[24] !== formatMessage || $[25] !== formatPrice || $[26] !== removeAllFilters || $[27] !== selectedFacetButton || $[28] !== userSelectedFacets) {
    t7 = userSelectedFacets.length > 0 && <><div className={styles["selected-facets__selected-facets-container"]}>{userSelectedFacets.map(selectedFacet_2 => selectedFacet_2.type !== "range" ? selectedFacet_2.values.map(t8 => {
          const {
            code: code_0,
            label: label_2
          } = t8;
          return selectedFacetButton(selectedFacet_2, code_0, label_2);
        }) : selectedFacetButton(selectedFacet_2, selectedFacet_2.code, `${formatPrice(selectedFacet_2.from)} - ${formatPrice(selectedFacet_2.to)}`))}<span className={styles["selected-facets__remove-all"]}><ButtonComponent buttonType={ButtonType.AS_TEXT} text={formatMessage("removeAllFilters")} onClick={removeAllFilters} /></span></div></>;
    $[24] = formatMessage;
    $[25] = formatPrice;
    $[26] = removeAllFilters;
    $[27] = selectedFacetButton;
    $[28] = userSelectedFacets;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== t7) {
    t8 = <div className={styles["selected-facets"]}>{t7}</div>;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
}
export default SelectedFacetsComponent;